import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Mountain,
  Phone,
  MessageCircle,
  Mail
} from "lucide-react";
import {
  useGetProgrammesQuery,
} from "../../apiCaller/apis/getApi";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import ClientLayout from "../../components/Layout/ClientLayout";
import SpinningLoader from "../../components/SpinningLoader/SpinningLoader";
import PaginationComponent from "../../components/pagination";
import ProgrammeSearchBarRedesign from "../../components/searchBar/ProgrammeSearchBarRedesign";
import ProgrammeHeroRedesign from "./components/ProgrammeHeroRedesign";
import ProgrammeCardRedesign from "./components/ProgrammeCardRedesign";

import Banner1 from "../../assets/images/banners/program1.jpg";

const ProgrammeListRedesign = () => {
  const [search, setSearch] = useState({ page: 1, totalItems: 5 });
  const {
    data: programmes,
    error: programmeError,
    isLoading: programmeIsLoading,
    isFetching: programmeIsFetching,
  } = GetTableListAPI(search, useGetProgrammesQuery);

  const navigate = useNavigate();

  const handleClick = (slug) => {
    navigate(`/programmes/${slug}`);
  };

  const handlePageClick = (page) => {
    const newSearch = { ...search, page: page + 1 };
    setSearch(newSearch);
  };

  const handleSearch = (params) => {
    const newSearch = { ...search, ...params };
    setSearch(newSearch);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  return (
    <ClientLayout>
      <div className="px-5">
        {/* Hero Component */}
        <ProgrammeHeroRedesign />

        {/* Main Content Section - Mamba Hikers Theme */}
        <section className="relative py-16 px-10 md:px-16 lg:px-48 bg-gradient-to-b from-white via-mamba-bg-light to-white">
          {/* Search Bar */}
          <div className="mb-12">
            <ProgrammeSearchBarRedesign handleSearch={handleSearch} />
          </div>

          {/* Programs List */}
          {programmeIsLoading || programmeIsFetching ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <SpinningLoader isLoading={true} hasData={false} />
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {programmes?.results.map((programme, index) => (
                <ProgrammeCardRedesign
                  key={programme.id}
                  programme={programme}
                  index={index}
                  handleClick={handleClick}
                  fadeInUp={fadeInUp}
                />
              ))}
            </motion.div>
          )}

          {/* Pagination */}
          {/* {!programmeIsLoading &&
            !programmeIsFetching &&
            programmes?.results?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-16"
              >
                <PaginationComponent
                  pageCount={programmes?.totalPages}
                  totalItems={programmes?.totalItems}
                  handlePageClick={handlePageClick}
                />
              </motion.div>
            )} */}

          {/* No Results Message - Mamba Theme */}
          {!programmeIsLoading &&
            !programmeIsFetching &&
            programmes?.results?.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="text-center py-20"
              >
                <div className="inline-flex p-6 bg-mamba-bg-light rounded-full mb-6 border-2 border-mamba-sky-blue/20">
                  <Mountain className="w-16 h-16 text-mamba-navy" />
                </div>
                <h3 className="text-2xl font-montserrat font-bold text-mamba-navy mb-2">
                  No Programs Found
                </h3>
                <p className="text-slate-600 font-inter">
                  Try adjusting your search criteria or check back later for new
                  programs.
                </p>
              </motion.div>
            )}
        </section>

        {/* Call to Action Section - Mamba Hikers Theme */}
        <section className="relative py-20 px-10 md:px-16 lg:px-32 overflow-hidden">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${Banner1})`,
              }}
            />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/90 to-mamba-navy/95" />

            {/* Animated Dots Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, rgb(212 165 116 / 0.3) 1px, transparent 0)`,
                  backgroundSize: "32px 32px",
                }}
              />
            </div>

            {/* Animated Background Elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -left-20 top-1/2 -translate-y-1/2 w-64 h-64 bg-mamba-golden/30 rounded-full blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
              className="absolute -right-20 bottom-1/4 w-80 h-80 bg-mamba-sky-blue/20 rounded-full blur-3xl"
            />

            {/* Content */}
            <div className="relative z-10 px-8 md:px-16 py-16 md:py-20 text-center">
              {/* Icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-8"
              >
                <div className="inline-flex p-6 rounded-full bg-gradient-to-br from-mamba-golden to-mamba-sky-blue shadow-2xl">
                  <Mountain className="w-12 h-12 md:w-16 md:h-16 text-white" />
                </div>
              </motion.div>

              {/* Heading */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-3xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-6 tracking-tight"
              >
                Ready to Start Your
                <span className="block bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent">
                  Training Journey?
                </span>
              </motion.h2>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-lg md:text-xl text-slate-200 mb-12 max-w-3xl mx-auto leading-relaxed font-inter"
              >
                Join the{" "}
                <span className="font-dancing italic text-mamba-golden">
                  Mamba Hikers
                </span>{" "}
                family and transform your hiking skills. Our expert guides are
                ready to help you choose the perfect program for your goals.
              </motion.p>

              {/* Contact Options */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
              >
                <motion.a
                  href="tel:+254700000000"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-mamba-golden to-amber-500 text-white font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-golden/50 transition-all duration-300"
                >
                  <Phone className="w-5 h-5" />
                  Call Us
                </motion.a>

                <motion.a
                  href="https://wa.me/254700000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-green-500/50 transition-all duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </motion.a>

                <motion.a
                  href="mailto:info@mambahikers.com?subject=Training%20Program%20Inquiry"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-sky-blue/50 transition-all duration-300"
                >
                  <Mail className="w-5 h-5" />
                  Email
                </motion.a>
              </motion.div>

              {/* Additional Info */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-wrap items-center justify-center gap-8 text-slate-300 text-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-mamba-golden" />
                  <span>Expert Guides</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-mamba-sky-blue" />
                  <span>Flexible Schedules</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-mamba-golden" />
                  <span>All Skill Levels</span>
                </div>
              </motion.div>
            </div>

            {/* Bottom Accent Line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-golden" />
          </div>
        </section>
      </div>
    </ClientLayout>
  );
};

export default ProgrammeListRedesign;
